import React from 'react'
import {Box, Flex, Image, Text} from '../../../utils/chakraMonomorphic'

// Imported from the concrete module (not the coreApi/faces barrel) so a test
// that auto-mocks the barrel's transport calls doesn't also stub out this pure
// URL builder. Mirrors FaceConfidenceModal.
import {buildTrainingPhotoImageUrl} from '../../../services/coreApi/faces/buildTrainingPhotoImageUrl'
import {PersonTrainingInfo} from '../../../types'
import copy from '../copy.json'
import {formatTimestamp} from '../formatters'

interface Props {
  training: PersonTrainingInfo | null
  personId: number
  clientId: number
}

export const TrainingPhotoPanel: React.FC<Props> = ({
  training,
  personId,
  clientId,
}): React.ReactElement => {
  // The <img> can 404 independently of the diagnostics call (S3 object gone,
  // grant issue) — surface that rather than leaving a broken-image icon.
  const [imageFailed, setImageFailed] = React.useState(false)

  React.useEffect(() => {
    setImageFailed(false)
  }, [personId, clientId])

  return (
    <Box mb={8} data-testid='training-photo-panel'>
      <Text fontSize='1.05rem' fontWeight='bold' mb={2}>
        {copy.training.heading}
      </Text>

      {!training ? (
        <Text fontSize='sm' color='gray.700' data-testid='training-none'>
          {copy.training.none}
        </Text>
      ) : (
        <Flex direction={['column', 'row']} gap={4} alignItems='flex-start'>
          {imageFailed ? (
            <Text
              fontSize='sm'
              color='gray.700'
              data-testid='training-image-failed'>
              {copy.training.imageFailed}
            </Text>
          ) : (
            <Image
              src={buildTrainingPhotoImageUrl(personId, clientId)}
              alt={copy.training.imageAlt}
              maxW='160px'
              borderRadius='md'
              onError={(): void => setImageFailed(true)}
              data-testid='training-image'
            />
          )}

          <Box fontSize='sm'>
            <Text data-testid='training-trained'>
              {copy.training.trained}: {formatTimestamp(training.trainedUTC)}
            </Text>
            {/* Only rendered when the person has actually been retrained —
                the server omits it when it would duplicate `trained`. */}
            {training.firstTrainedUTC && (
              <Text color='gray.700' data-testid='training-first-trained'>
                {copy.training.firstTrained}:{' '}
                {formatTimestamp(training.firstTrainedUTC)}
              </Text>
            )}
            <Text color='gray.700'>
              {copy.training.enrolledFaces}: {training.enrolledFaceCount}
            </Text>
          </Box>
        </Flex>
      )}
    </Box>
  )
}
